/**
 * Derives a { parent, page } label pair from a pathname for Firefly PAGE_VIEW events.
 *
 * The mapping is built at module load time by walking sideNavigationArray(null).
 * - Parent  = the top-level LHN section label  (e.g. "Intake", "Programs")
 * - Page    = the leaf nav item label           (e.g. "Create Request", "Program List")
 *
 * Because this is driven entirely from SideNavigationItems, any path/label change
 * or new LHN entry in that file is automatically reflected here with no extra work.
 *
 * Entries are sorted longest-path-first so that more specific prefixes always win
 * (e.g. /sourcing-groups/edit matches before /sourcing-groups).
 */

import { sideNavigationArray } from '../SideNavigation/SideNavigationItems'

export interface PageLabel {
  parent: string
  page: string
}

interface NavEntry {
  path: string
  parent: string
  page: string
}

const buildNavEntries = (): NavEntry[] => {
  // Pass null — all entitlement checks use optional chaining so the full
  // structure is returned regardless of entitlement values.
  const items = sideNavigationArray(null)
  const entries: NavEntry[] = []

  items.forEach((item: any) => {
    if (item.children) {
      // Group item — item.label is the parent section
      item.children.forEach((child: any) => {
        if (child.path && typeof child.path === 'string') {
          entries.push({
            path: child.path,
            parent: item.label as string,
            page: child.label as string,
          })
        }
      })
    } else if (item.path && typeof item.path === 'string') {
      // Leaf item at the top level — parent and page are the same label
      entries.push({
        path: item.path,
        parent: item.label as string,
        page: item.label as string,
      })
    }
  })

  // Longer paths must be checked before shorter ones to avoid false prefix matches
  // e.g. /sourcing-groups/edit must win over /sourcing-groups
  return entries.sort((a, b) => b.path.length - a.path.length)
}

const NAV_ENTRIES: NavEntry[] = buildNavEntries()

export const getPageLabel = (pathname: string): PageLabel => {
  const match = NAV_ENTRIES.find((entry) => pathname.startsWith(entry.path))

  if (match) {
    return { parent: match.parent, page: match.page }
  }

  return { parent: 'Unknown', page: pathname }
}
